import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

const aboutLinks = [
  { label: "Our Story", href: "/about#our-story" },
  { label: "Mission & Vision", href: "/about#mission" },
  { label: "Values & Policies", href: "/about#values" },
  { label: "Partners & Donors", href: "/partners-donors" },
  { label: "Our Team", href: "/our-team" },
];

const workLinks = [
  { label: "Our Work", href: "/our-work" },
  { label: "Health", href: "/our-work/health" },
  { label: "Education", href: "/our-work/education" },
  { label: "Economic Security", href: "/our-work/economic-security" },
  { label: "Protection", href: "/our-work/protection" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [workOpen, setWorkOpen] = useState(false);
  const aboutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const workTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Scroll to top on route change (unless it's a hash link)
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [location.pathname]);

  const openAbout = () => {
    if (aboutTimer.current) clearTimeout(aboutTimer.current);
    setAboutOpen(true);
  };
  const closeAbout = () => {
    aboutTimer.current = setTimeout(() => setAboutOpen(false), 150);
  };

  const openWork = () => {
    if (workTimer.current) clearTimeout(workTimer.current);
    setWorkOpen(true);
  };
  const closeWork = () => {
    workTimer.current = setTimeout(() => setWorkOpen(false), 150);
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "bg-dark/90 backdrop-blur-md shadow-lg"
          : "bg-transparent"
      }`}
    >
      <div className="px-4 md:px-8 lg:px-12 py-3 md:py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 text-white hover:text-primary transition-colors duration-300">
          <img
            src="https://storage.readdy-site.link/project_files/4e9a12fb-cddb-4082-86d9-a8d48c5e1af1/4c1dbfd3-51c3-4b4d-bce2-d21c130722c0_LOGO---BHODEV.jpeg?v=bec7ea05bbbc33a50ca492c9bcdd08aa"
            alt="BHODEV Logo"
            title="BHODEV Cameroon Official Logo"
            className="w-10 h-10 rounded-full object-cover ring-2 ring-white/20"
          />
          <div className="hidden sm:block">
            <p className="text-xs font-semibold leading-tight tracking-wide">Bogi Health Organization for Development</p>
            <p className="text-xs text-primary font-bold tracking-wider">(BHODEV)</p>
          </div>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-7 font-inter font-medium text-sm text-white/90">
          {/* About Us Dropdown */}
          <div
            className="relative"
            onMouseEnter={openAbout}
            onMouseLeave={closeAbout}
          >
            <Link
              to="/about"
              className="flex items-center gap-1 hover:text-primary transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              About Us <i className="ri-arrow-down-s-line text-base"></i>
            </Link>
            {aboutOpen && (
              <div
                className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-2xl py-2 min-w-[200px] z-50 border border-stone-100 overflow-hidden"
                onMouseEnter={openAbout}
                onMouseLeave={closeAbout}
              >
                {aboutLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="block px-4 py-2.5 text-stone-700 hover:bg-secondary hover:text-primary text-sm transition-colors duration-200"
                    onClick={() => setAboutOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/get-involved" className="hover:text-primary transition-colors duration-300 whitespace-nowrap">Get Involved</Link>
          <Link to="/news" className="hover:text-primary transition-colors duration-300">News</Link>

          {/* Our Work Dropdown */}
          <div
            className="relative"
            onMouseEnter={openWork}
            onMouseLeave={closeWork}
          >
            <Link
              to="/our-work"
              className="flex items-center gap-1 hover:text-primary transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              Our Work <i className="ri-arrow-down-s-line text-base"></i>
            </Link>
            {workOpen && (
              <div
                className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-2xl py-2 min-w-[200px] z-50 border border-stone-100 overflow-hidden"
                onMouseEnter={openWork}
                onMouseLeave={closeWork}
              >
                {workLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="block px-4 py-2.5 text-stone-700 hover:bg-secondary hover:text-primary text-sm transition-colors duration-200"
                    onClick={() => setWorkOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/contact" className="hover:text-primary transition-colors duration-300">Contact</Link>

          <Link
            to="/donate"
            className="bg-primary text-white px-5 py-2.5 rounded-full hover:bg-primary-dark transition-all duration-300 whitespace-nowrap font-semibold text-sm shadow-lg shadow-primary/25 hover:shadow-primary/40"
          >
            Donate Now
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden text-white text-2xl w-10 h-10 flex items-center justify-center cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={mobileOpen ? "ri-close-line" : "ri-menu-line"}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-dark/95 backdrop-blur-lg border-t border-white/10 px-4 py-5 flex flex-col gap-3 text-white font-inter text-sm">
          <p className="font-semibold text-white/40 uppercase text-xs tracking-wider">About Us</p>
          {aboutLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className="pl-3 hover:text-primary transition-colors duration-200"
              onClick={() => setMobileOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          <Link to="/get-involved" className="hover:text-primary transition-colors duration-200" onClick={() => setMobileOpen(false)}>Get Involved</Link>
          <Link to="/news" className="hover:text-primary transition-colors duration-200" onClick={() => setMobileOpen(false)}>News</Link>
          <p className="font-semibold text-white/40 uppercase text-xs tracking-wider mt-2">Our Work</p>
          {workLinks.map((link) => (
            <Link key={link.label} to={link.href} className="pl-3 hover:text-primary transition-colors duration-200" onClick={() => setMobileOpen(false)}>
              {link.label}
            </Link>
          ))}
          <Link to="/contact" className="hover:text-primary transition-colors duration-200" onClick={() => setMobileOpen(false)}>Contact</Link>
          <Link
            to="/donate"
            className="bg-primary text-white px-4 py-3 rounded-full text-center font-semibold mt-2"
            onClick={() => setMobileOpen(false)}
          >
            Donate Now
          </Link>
        </div>
      )}
    </nav>
  );
}
